import { useState } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import {
  getJobQueue,
  getPendingJobs,
  retryJob,
  dismissJob,
  purgeDeadJobs,
  retryAllDeadJobs,
  type DeadJob,
  type PendingJobRow,
  type ActiveJob,
} from "@workspace/api-client-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LogoMark } from "@/components/Logo";
import {
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RotateCcw,
  Inbox,
  Zap,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function fmtSeconds(s: number | null): string {
  if (s == null) return "—";
  if (s < 1) return `${Math.round(s * 1000)}ms`;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${(s / 60).toFixed(1)}m`;
}

const JOB_TYPE_LABELS: Record<string, string> = {
  ocr_verify: "OCR Verify",
  generate_tax_cert: "Tax Cert",
  generate_1099c: "1099-C",
  send_email: "Send Email",
  payout_execute: "Payout",
  fraud_score: "Fraud Score",
  zip_export: "ZIP Export",
};

function jobLabel(type: string): string {
  return JOB_TYPE_LABELS[type] ?? type;
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    running: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    failed: "bg-orange-100 text-orange-800 border-orange-200",
    dead: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? "bg-gray-100 text-gray-700 border-gray-200"}`}
    >
      {status}
    </span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: "green" | "red" | "blue" | "default";
}) {
  const accentClass = {
    green: "text-green-600",
    red: "text-red-600",
    blue: "text-blue-600",
    default: "text-foreground",
  }[accent ?? "default"];

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${accentClass}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminJobsQueue() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pendingPage, setPendingPage] = useState(1);

  const {
    data: queue,
    isLoading: queueLoading,
    isError: queueError,
    refetch: refetchQueue,
    isFetching,
  } = useQuery({
    queryKey: ["admin-job-queue"],
    queryFn: getJobQueue,
    refetchInterval: 15_000,
  });

  const { data: pendingJobs, isLoading: pendingLoading } = useQuery({
    queryKey: ["admin-pending-jobs", pendingPage],
    queryFn: () => getPendingJobs(pendingPage),
    refetchInterval: 15_000,
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => retryJob(id),
    onSuccess: (_, id) => {
      toast({ title: "Job re-queued", description: `Job #${id} has been reset to pending.` });
      void queryClient.invalidateQueries({ queryKey: ["admin-job-queue"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-pending-jobs"] });
    },
    onError: () => {
      toast({ title: "Retry failed", description: "Could not re-queue the job.", variant: "destructive" });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: number) => dismissJob(id),
    onSuccess: (_, id) => {
      toast({ title: "Job dismissed", description: `Job #${id} has been removed.` });
      void queryClient.invalidateQueries({ queryKey: ["admin-job-queue"] });
    },
    onError: () => {
      toast({ title: "Dismiss failed", description: "Could not dismiss the job.", variant: "destructive" });
    },
  });

  const purgeMutation = useMutation({
    mutationFn: purgeDeadJobs,
    onSuccess: (data) => {
      toast({ title: "Dead queue purged", description: `${data.purged} job${data.purged !== 1 ? "s" : ""} removed.` });
      void queryClient.invalidateQueries({ queryKey: ["admin-job-queue"] });
    },
    onError: () => {
      toast({ title: "Purge failed", description: "Could not purge dead jobs.", variant: "destructive" });
    },
  });

  const retryAllMutation = useMutation({
    mutationFn: retryAllDeadJobs,
    onSuccess: (data) => {
      toast({ title: "All jobs re-queued", description: `${data.requeued} job${data.requeued !== 1 ? "s" : ""} reset to pending.` });
      void queryClient.invalidateQueries({ queryKey: ["admin-job-queue"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-pending-jobs"] });
    },
    onError: () => {
      toast({ title: "Re-queue failed", description: "Could not re-queue all dead jobs.", variant: "destructive" });
    },
  });

  const summary = queue?.summary;
  const byType = queue?.byType ?? {};
  const deadJobs: DeadJob[] = queue?.deadJobs ?? [];
  const activeJobs: ActiveJob[] = queue?.activeJobs ?? [];

  const [deadTypeFilter, setDeadTypeFilter] = useState<string>("all");
  const [deadSearchFilter, setDeadSearchFilter] = useState<string>("");

  const deadJobTypes = Array.from(new Set(deadJobs.map((j) => j.type))).sort();

  const filteredDeadJobs = deadJobs.filter((job) => {
    if (deadTypeFilter !== "all" && job.type !== deadTypeFilter) return false;
    if (deadSearchFilter.trim()) {
      const q = deadSearchFilter.trim().toLowerCase();
      const inType = jobLabel(job.type).toLowerCase().includes(q) || job.type.toLowerCase().includes(q);
      const inError = (job.error ?? "").toLowerCase().includes(q);
      if (!inType && !inError) return false;
    }
    return true;
  });

  const totalPending = Object.values(byType).reduce((s, t) => s + t.pending, 0);
  const totalRunning = Object.values(byType).reduce((s, t) => s + t.running, 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div className="flex items-center gap-2">
              <LogoMark size={28} />
              <span className="font-semibold text-white">Job Queue</span>
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchQueue()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <UserButton />
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {queueLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : queueError ? (
          <Card className="border-destructive">
            <CardContent className="pt-6 flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <p>Failed to load job queue data. Please refresh.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* 24 h summary */}
            <section>
              <h2 className="text-lg font-semibold mb-4">Last 24 Hours</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  label="Jobs processed"
                  value={summary?.total24h ?? 0}
                />
                <StatCard
                  label="Completed"
                  value={summary?.completed24h ?? 0}
                  accent="green"
                />
                <StatCard
                  label="Failed / Dead"
                  value={summary?.failed24h ?? 0}
                  accent={summary?.failed24h ? "red" : "default"}
                />
                <StatCard
                  label="Success rate"
                  value={summary?.successRate != null ? `${summary.successRate}%` : "—"}
                  sub={`Avg ${fmtSeconds(summary?.avgProcessingSeconds ?? null)} per job`}
                  accent={
                    summary?.successRate == null
                      ? "default"
                      : summary.successRate >= 95
                        ? "green"
                        : summary.successRate >= 80
                          ? "default"
                          : "red"
                  }
                />
              </div>
            </section>

            {/* Live queue */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Live Queue</h2>
                <div className="flex gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {totalPending} pending
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="h-3.5 w-3.5 text-blue-500" />
                    {totalRunning} running
                  </span>
                </div>
              </div>
              {Object.keys(byType).length === 0 ? (
                <Card>
                  <CardContent className="pt-6 flex flex-col items-center gap-2 py-12 text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                    <p className="text-sm">Queue is empty — no pending or running jobs.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job type</TableHead>
                        <TableHead className="text-right">Pending</TableHead>
                        <TableHead className="text-right">Running</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(byType).map(([type, counts]) => (
                        <TableRow key={type}>
                          <TableCell className="font-medium">{jobLabel(type)}</TableCell>
                          <TableCell className="text-right">
                            {counts.pending > 0 ? (
                              <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50">
                                {counts.pending}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {counts.running > 0 ? (
                              <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                                {counts.running}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </section>

            {/* Running jobs */}
            {activeJobs.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold mb-4">Currently Running</h2>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Attempts</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Queued</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeJobs.map((job: ActiveJob) => (
                        <TableRow key={job.id}>
                          <TableCell className="font-mono text-sm">#{job.id}</TableCell>
                          <TableCell>{jobLabel(job.type)}</TableCell>
                          <TableCell>{job.attempts}</TableCell>
                          <TableCell>{job.startedAt ? relativeTime(job.startedAt) : "—"}</TableCell>
                          <TableCell>{relativeTime(job.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </section>
            )}

            {/* Dead-letter jobs */}
            <section>
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">Dead-Letter Queue</h2>
                  {deadJobs.length > 0 && (
                    <Badge className="bg-red-100 text-red-700 border-red-200 border">
                      {deadJobs.length}
                    </Badge>
                  )}
                </div>
                {deadJobs.length > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={retryAllMutation.isPending || purgeMutation.isPending}
                        >
                          {retryAllMutation.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                          )}
                          {retryAllMutation.isPending ? "Re-queuing..." : "Re-queue All"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Re-queue all dead jobs?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will reset all {deadJobs.length} dead job{deadJobs.length !== 1 ? "s" : ""} back to pending so the worker can process them again. Each re-queue will be recorded in the audit log.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => retryAllMutation.mutate()}>
                            Re-queue All
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={purgeMutation.isPending || retryAllMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Purge all dead jobs
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Permanently delete all dead jobs?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove all {deadJobs.length} dead job{deadJobs.length !== 1 ? "s" : ""} from the queue. This cannot be undone and any diagnostic information they contain will be lost.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => purgeMutation.mutate()}
                          >
                            Purge all
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              {deadJobs.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      className="pl-8 h-8 text-sm"
                      placeholder="Search by type or error…"
                      value={deadSearchFilter}
                      onChange={(e) => setDeadSearchFilter(e.target.value)}
                    />
                    {deadSearchFilter && (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setDeadSearchFilter("")}
                        aria-label="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <Select value={deadTypeFilter} onValueChange={setDeadTypeFilter}>
                    <SelectTrigger className="w-40 h-8 text-sm">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {deadJobTypes.map((t) => (
                        <SelectItem key={t} value={t}>{jobLabel(t)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(deadTypeFilter !== "all" || deadSearchFilter) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs text-muted-foreground"
                      onClick={() => { setDeadTypeFilter("all"); setDeadSearchFilter(""); }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}
              {deadJobs.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 flex flex-col items-center gap-2 py-12 text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                    <p className="text-sm">No dead-letter jobs — nothing to investigate.</p>
                  </CardContent>
                </Card>
              ) : filteredDeadJobs.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 flex flex-col items-center gap-2 py-12 text-muted-foreground">
                    <Search className="h-8 w-8" />
                    <p className="text-sm">No dead jobs match your filter.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredDeadJobs.map((job: DeadJob) => (
                    <Card key={job.id} className="border-red-200">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm text-muted-foreground">#{job.id}</span>
                            <span className="font-semibold">{jobLabel(job.type)}</span>
                            <StatusBadge status={job.status} />
                            <span className="text-xs text-muted-foreground">
                              {job.attempts}/{job.maxAttempts} attempts · last updated {relativeTime(job.updatedAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={retryMutation.isPending || dismissMutation.isPending}
                              onClick={() => retryMutation.mutate(job.id)}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1" />
                              Re-queue
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive hover:bg-red-50"
                              disabled={dismissMutation.isPending || retryMutation.isPending}
                              onClick={() => dismissMutation.mutate(job.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        {job.error && (
                          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
                            <p className="text-xs font-medium text-red-700 mb-0.5">Error</p>
                            <p className="text-sm text-red-900 font-mono whitespace-pre-wrap break-all">
                              {job.error}
                            </p>
                          </div>
                        )}
                        <details className="text-xs text-muted-foreground">
                          <summary className="cursor-pointer hover:text-foreground">View payload</summary>
                          <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                            {JSON.stringify(job.payload, null, 2)}
                          </pre>
                        </details>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>

            {/* Pending queue (paginated) */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Pending Queue</h2>
                {pendingJobs && (
                  <span className="text-sm text-muted-foreground">
                    {pendingJobs.total} jobs total
                  </span>
                )}
              </div>
              {pendingLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !pendingJobs || pendingJobs.rows.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 flex flex-col items-center gap-2 py-12 text-muted-foreground">
                    <Inbox className="h-8 w-8" />
                    <p className="text-sm">No pending jobs.</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Priority</TableHead>
                          <TableHead>Attempts</TableHead>
                          <TableHead>Run after</TableHead>
                          <TableHead>Queued</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingJobs.rows.map((job: PendingJobRow) => (
                          <TableRow key={job.id}>
                            <TableCell className="font-mono text-sm">#{job.id}</TableCell>
                            <TableCell>{jobLabel(job.type)}</TableCell>
                            <TableCell>{job.priority}</TableCell>
                            <TableCell>{job.attempts}</TableCell>
                            <TableCell>{relativeTime(job.runAfter)}</TableCell>
                            <TableCell>{relativeTime(job.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                  {pendingJobs.pages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPendingPage((p) => Math.max(1, p - 1))}
                        disabled={pendingPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {pendingPage} of {pendingJobs.pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPendingPage((p) => Math.min(pendingJobs.pages, p + 1))}
                        disabled={pendingPage === pendingJobs.pages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </div>
      <QuoteCarousel />

    </div>
  );
}
